import { flushArray, isEmptyObject } from "@zyzgroup/core-common";
import { HTMLEventObservable } from "../useEventListener";
import { createElement, empty, insert } from "../useElement";
import {
  tap$,
  useMouseAndPointerDownMovePoint$,
  useMousePoint,
  usePointerDownMovePoint$
} from "../useElementEvents";
import {
  atomWriteAttribute,
  cssAttrsToCanvasAttrs,
  useAttribute
} from "../useAttribute";
import { Shape, ShapeCombo } from "./Shape";
import {
  createSVGElement,
  createSvgPath,
  downloadFromSVGElement,
  isSVGElementPath
} from "./svg";
import { YZCanvasContext } from "./canvas";
import { Matrix, toRadian } from "../../math";
import { Color } from "../../color";
import { BehaviorSubject } from "rxjs";
import { useResize$ } from "../useResize";
import { setStyle } from "../useStyle";
import { waitElementAddedInDOM } from "../useMutation";

// d3
// https://jsfiddle.net/user/raphaeljs/fiddles

export type DrawShape = Shape | ShapeCombo;

export type DrawType = "svg" | "canvas" | "div";

export class DrawContext {
  public PARENT_EL: HTMLEventObservable;

  public SVG_EL: SVGElement | null = null;

  public CANVAS_EL: HTMLCanvasElement | null = null;
  public ctx: YZCanvasContext | null = null;

  public rect = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };
  public size$ = new BehaviorSubject<DOMRect | null>(null);
  private resizeSubscription: any;

  public mpg = useMousePoint();
  private mouseSubscription: any;

  public shapes: Shape[] = [];

  public shapeTap$ = new BehaviorSubject<Shape | null>(null);
  private tapSubscription: any;

  private lastMouseInShapeId = "";
  // 一旦拖拽开始，不释放时，可以一直触发
  private draggingShapeId = "";
  private prevCursor = "default";

  constructor(
    PARENT_EL: HTMLEventObservable = document.documentElement,
    type: DrawType = "canvas",
    enableCustomContext = false // true 影响性能
  ) {
    this.PARENT_EL = PARENT_EL;

    if (type == "svg" || type == "canvas") {
      empty(this.PARENT_EL);
    }

    if (type == "svg") {
      this.SVG_EL = createSVGElement("svg") as unknown as SVGElement;
      insert(this.PARENT_EL, this.SVG_EL);
    }

    if (type == "canvas") {
      this.CANVAS_EL = createElement("canvas") as unknown as HTMLCanvasElement;
      insert(this.PARENT_EL, this.CANVAS_EL);
    }

    this.resizeSubscription = useResize$(this.PARENT_EL).subscribe(
      (r: DOMRect) => {
        this.rect.x = r.x;
        this.rect.y = r.y;
        this.rect.width = r.width;
        this.rect.height = r.height;
        if (this.SVG_EL) {
          useAttribute(this.SVG_EL, {
            viewBox: `0 0 ${this.rect.width} ${this.rect.height}`,
            width: `${this.rect.width}`,
            height: `${this.rect.height}`
          });
        }
        if (this.CANVAS_EL) {
          useAttribute(this.CANVAS_EL, {
            width: `${this.rect.width}`,
            height: `${this.rect.height}`
          });
          this.ctx = new YZCanvasContext(
            this.CANVAS_EL,
            this.rect.width,
            this.rect.height,
            enableCustomContext
          );
        }
        this.size$.next(r);
      }
    );

    this.mouseSubscription = useMouseAndPointerDownMovePoint$(
      this.SVG_EL || this.CANVAS_EL || this.PARENT_EL,
      {
        useMousePoint: this.mpg
      }
    ).subscribe(() => {});

    this.tapSubscription = tap$(this.PARENT_EL).subscribe((e) => {
      for (const shape of this.shapes) {
        if (shape.isMouseIn) {
          this.shapeTap$.next(shape);
          // if (shape.parent) {
          //   shape.parent.combo.shapes.forEach((s) => {
          //     if (s !== shape) {
          //       this.shapeTap$.next(s);
          //     }
          //   });
          // }
          break;
        }
      }
    });
  }

  public destroy() {
    this.resizeSubscription.unsubscribe();
    this.mouseSubscription.unsubscribe();
    this.tapSubscription.unsubscribe();
  }

  private setCursor(
    cursor: "default" | "pointer" | "grab" | "grabbing" = "default"
  ) {
    if (cursor != this.prevCursor) {
      this.PARENT_EL.style.cursor = cursor;
      this.prevCursor = cursor;
    }
  }

  // requestAnimationFrameCycle()
  public draw(...ss: DrawShape[]) {
    // 传入空数组：保持原 shapes 以提高性能
    if (ss.length) {
      const frameShapes: Shape[] = [];
      ss.forEach((s) => {
        if (s instanceof ShapeCombo) {
          frameShapes.push(...s.shapes);
        } else {
          frameShapes.push(s);
        }
      });

      // 删除不需要的 shape el
      if (this.SVG_EL) {
        this.shapes
          .filter(
            (shape) => frameShapes.findIndex((s) => s.id == shape.id) == -1
          )
          .forEach((shape) => {
            if (shape.el) {
              shape.el.remove();
              delete shape.el;
            }
          });
      }

      flushArray(this.shapes, frameShapes);
    }

    if (this.ctx) {
      // draw in Canvas
      this.ctx.clearRect();
    }

    this.lastMouseInShapeId = "";
    if (!this.mpg.current.isDown) {
      this.draggingShapeId = "";
    }

    for (const shape of this.shapes) {
      // transform origin - 不缓存, 直接使用 getBoundingClientRectCenter， 间接使用 getBoundingClientRect
      const { x: centerX, y: centerY } = shape.transformOrigin();
      let centerXSelf = 0;
      let centerYSelf = 0;
      if (
        shape.parent?.dDegree ||
        shape.parent?.dScaleX !== 1 ||
        shape.parent?.dScaleY !== 1
      ) {
        const tos = shape.transformOrigin(true);
        centerXSelf = tos.x;
        centerYSelf = tos.y;
      }

      if (shape.shouldUpdatePathD) {
        // 强制更新
        shape.pathD();
      }

      if (this.SVG_EL) {
        // draw in SVG
        if (!shape.el || shape.shouldUpdateSVGElInDraw) {
          const newEl = shape.toSVGEl();
          if (!shape.el) {
            // 新增
            shape.el = newEl || createSvgPath();
            insert(this.SVG_EL, shape.el);
            useAttribute(shape.el, { id: shape.id });
            // 异步更新问题未解决！
            // waitElementAddedInDOM(this.SVG_EL, shape.el).then(() => {
            //   console.log(shape.id, "added to DOM!");
            // });
          } else {
            // 只更新 toSVGEl()
            if (newEl) {
              shape.el.remove();
              shape.el = newEl;
              insert(this.SVG_EL, shape.el);
              useAttribute(shape.el, { id: shape.id });
            }
          }
          if (isSVGElementPath(shape.el)) {
            useAttribute(shape.el, { d: shape.toSVGPathD() });
          }
          shape.shouldUpdateSVGElInDraw = false;
          shape.shouldUpdateAttrsInDraw = true;
          shape.shouldUpdateStyleInDraw = true;
          shape.shouldUpdateMInDraw = true;
        }
        if (shape.shouldUpdateAttrsInDraw) {
          if (!isEmptyObject(shape.attrs)) {
            useAttribute(shape.el, shape.attrs);
          }
          shape.shouldUpdateAttrsInDraw = false;
        }
        if (shape.shouldUpdateStyleInDraw) {
          if (!isEmptyObject(shape.style)) {
            setStyle(shape.el, shape.style);
          }
          shape.shouldUpdateStyleInDraw = false;
        }
        // 性能瓶颈
        if (shape.shouldUpdateMInDraw || !shape.m) {
          // 移动的是坐标系 the order of these are important !
          const m = new Matrix();
          // 公共
          m.translate(
            shape.x + (shape.parent?.dX || 0),
            shape.y + (shape.parent?.dY || 0)
          );
          m.rotate(shape.degree, centerX, centerY);
          m.scale(shape.scaleX, shape.scaleY, centerX, centerY);
          // 自己
          if (shape.parent?.dDegree) {
            m.rotate(shape.parent.dDegree, centerXSelf, centerYSelf);
          }
          if (shape.parent?.dScaleX !== 1 || shape.parent?.dScaleY !== 1) {
            m.scale(
              shape.parent?.dScaleX || 1,
              shape.parent?.dScaleY || 1,
              centerXSelf,
              centerYSelf
            );
          }
          useAttribute(shape.el, { transform: m.toString() });
          // 缓存计算结果
          shape.m = m;
          shape.shouldUpdateMInDraw = false;
        }
      }
      if (this.ctx) {
        // draw in Canvas
        this.ctx.save();
        // 移动的是坐标系 the order of these are important !
        // 公共
        this.ctx.translate(
          shape.x + (shape.parent?.dX || 0),
          shape.y + (shape.parent?.dY || 0)
        );
        this.ctx.rotate(toRadian(shape.degree), centerX, centerY);
        this.ctx.scale(shape.scaleX, shape.scaleY, centerX, centerY);
        // 自己
        if (shape.parent?.dDegree) {
          this.ctx.rotate(
            toRadian(shape.parent.dDegree),
            centerXSelf,
            centerYSelf
          );
        }
        if (shape.parent?.dScaleX !== 1 || shape.parent?.dScaleY !== 1) {
          this.ctx.scale(
            shape.parent?.dScaleX || 1,
            shape.parent?.dScaleY || 1,
            centerXSelf,
            centerYSelf
          );
        }
        // 缓存计算结果
        shape.m = this.ctx.currentMatrix;
        cssAttrsToCanvasAttrs(this.ctx, shape.attrs);
        shape.toCanvasContext(this.ctx);
        if (this.ctx.hasSetCustomStrokeStyle) {
          this.ctx.stroke();
        }
        if (this.ctx.hasSetCustomFillStyle) {
          this.ctx.fill();
        }
        // // 画出本次 path2d
        // this.ctx.save();
        // this.ctx.lineWidth = 4;
        // this.ctx.strokeStyle = "#ff0000";
        // this.const path2d = ctx.currentPath2D;
        // this.ctx.stroke(path2d);
        // this.ctx.restore();
      }

      // 性能影响，可关闭检测
      if (shape.interactive) {
        // isMouseIn
        if (this.SVG_EL) {
          shape.isMouseIn =
            shape.isPointInPath(
              this.SVG_EL,
              this.mpg.current.x,
              this.mpg.current.y
            ) ||
            shape.isPointInStroke(
              this.SVG_EL,
              this.mpg.current.x,
              this.mpg.current.y
            );
        } else if (this.ctx) {
          shape.isMouseIn =
            shape.isPointInPath(
              this.ctx,
              this.mpg.current.x,
              this.mpg.current.y
            ) ||
            shape.isPointInStroke(
              this.ctx,
              this.mpg.current.x,
              this.mpg.current.y
            );
        } else {
          shape.isMouseIn = false;
        }
      } else {
        shape.isMouseIn = false;
      }

      if (shape.isMouseIn) {
        this.setCursor("pointer");
        this.lastMouseInShapeId = shape.id;
      }

      // isDragging
      shape.isDragging = shape.id == this.draggingShapeId;
      if (
        this.mpg.current.isDown &&
        (shape.isDragging ||
          (!this.draggingShapeId &&
            shape.isMouseIn &&
            shape.drag &&
            shape.drag.enable))
      ) {
        this.setCursor("grabbing");
        shape.isDragging = true;
        this.draggingShapeId = shape.id;
        let dx = this.mpg.current.x - this.mpg.start.x;
        let dy = this.mpg.current.y - this.mpg.start.y;
        if (dx || dy) {
          if (shape.drag && shape.drag.enable && shape.drag.alongPath) {
            const closestPoint = shape.drag.alongPath
              .transform({ m: shape.m })
              .closestPoint(this.mpg.current.x, this.mpg.current.y);
            dx = closestPoint.point.x - shape.x;
            dy = closestPoint.point.y - shape.y;
          }
          if (dx || dy) {
            shape.move(dx, dy);
            shape.parent?.combo.shapes.forEach((s) => {
              if (s.drag?.followSibling && s !== shape) {
                s.move(dx, dy);
              }
            });
          }
        }
        this.mpg.setStart(this.mpg.current);
      } else if (shape.isMouseIn && shape.drag && shape.drag.enable) {
        this.setCursor("grab");
      }

      if (this.ctx) {
        this.ctx.restore();
      }
    }

    if (!this.lastMouseInShapeId && !this.draggingShapeId) {
      this.setCursor("default");
    }
  }

  public clear() {
    this.shapes.forEach((shape) => {
      if (shape.el) {
        shape.el.remove();
        delete shape.el;
      }
    });
    if (this.ctx) {
      this.ctx.clearRect();
    }
    this.shapes = [];
  }

  public drawMouseMoveLine(options?: {
    brushSize?: number;
    brushColor?: string;
  }) {
    const brushSize = options?.brushSize || 4;
    const brushColor = options?.brushColor || "#000000";
    let hasSetMoveTo = false;
    const s = usePointerDownMovePoint$(
      this.SVG_EL || this.CANVAS_EL || this.PARENT_EL,
      {
        start: () => {
          hasSetMoveTo = false;
          if (this.ctx) {
            this.ctx.save();
            this.ctx.lineWidth = brushSize;
            this.ctx.strokeStyle = brushColor;
            this.ctx.beginPath();
          }
        },
        end: () => {
          if (this.ctx) {
            // this.ctx.closePath();
            this.ctx.restore();
          }
        }
      }
    ).subscribe((mpg) => {
      if (this.ctx) {
        if (!hasSetMoveTo) {
          hasSetMoveTo = true;
          this.ctx.moveTo(mpg.current.x, mpg.current.y);
        } else {
          this.ctx.lineTo(mpg.current.x, mpg.current.y);
        }
        this.ctx.stroke();
      }
    });
    return () => {
      s.unsubscribe();
    };
  }

  public drawMouseMoveBrush(options?: {
    brushSize?: number;
    brushDensity?: number;
    brushColor?: string;
  }) {
    const brushSize = options?.brushSize || 10;
    const brushDensity = options?.brushDensity || 50;
    const brushColor = options?.brushColor || "#000000";
    const brushColorNumber = new Color(brushColor);
    const r = brushColorNumber.r;
    const g = brushColorNumber.g;
    const b = brushColorNumber.b;
    const s = usePointerDownMovePoint$(
      this.SVG_EL || this.CANVAS_EL || this.PARENT_EL
    ).subscribe((mpg) => {
      if (this.ctx) {
        const imageData = this.ctx.getImageData(
          0,
          0,
          this.ctx.canvas.width,
          this.ctx.canvas.height
        );
        const pixels = imageData.data;
        for (let i = 0; i < brushDensity; i++) {
          const angle = Math.random() * Math.PI * 2;
          const radius = Math.random() * brushSize;
          const xpos = (mpg.current.x + Math.cos(angle) * radius) | 0;
          const ypos = (mpg.current.y + Math.sin(angle) * radius) | 0;
          const offset = (xpos + ypos * imageData.width) * 4; // array index of canvas coordinate
          // set the color of a pixel using its component colors: r,g,b,a (0-255)
          pixels[offset] = r; // red
          pixels[offset + 1] = g; // green
          pixels[offset + 2] = b; // blue
          pixels[offset + 3] = 255; // alpha
        }
        this.ctx.putImageData(imageData);
      }
    });
    return () => {
      s.unsubscribe();
    };
  }

  public downloadSVG = () => {
    if (this.SVG_EL) {
      downloadFromSVGElement(this.SVG_EL);
    }
  };
}
